import { useActiveSession } from '@texas/hooks/useSession';
import { Box, Button, Flex, Tag, Text } from '@chakra-ui/react';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { TrackerWrapper } from '@texas/components/shared/tracker/TrackerWrapper';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useEntityHistory } from '@texas/hooks/useEntityHistory';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  onNavigate: () => void;
}

const PEEK_ENTRIES = 3;

function getEntityType(type: string): EntityType | null {
  switch (type) {
    case 'customer':
      return EntityType.Customer;
    case 'inquiry':
      return EntityType.Inquiry;
    case 'offer':
      return EntityType.Offer;
    default:
      return null;
  }
}

export function RecentEntities({ onNavigate }: Props) {
  const {
    currentUser: { betaFeatures },
  } = useActiveSession();
  const history = useEntityHistory().filter(
    (entry) =>
      betaFeatures || (entry.type !== 'inquiry' && entry.type !== 'offer'),
  );
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (history.length === 0) return null;

  const visibleHistory = expanded ? history : history.slice(0, PEEK_ENTRIES);
  const canExpand = !expanded && history.length > PEEK_ENTRIES;

  return (
    <Box mt={3}>
      <Text
        fontSize="xs"
        variant="sub"
        mb={2}
        textTransform="uppercase"
        letterSpacing="wider"
      >
        {t('general.recentlyVisited')}
      </Text>
      <Flex direction="column" gap={1}>
        {visibleHistory.map((entry) => {
          const entityType = getEntityType(entry.type);
          const link = (
            <AngularNavLink
              to={entry.to}
              navobj={entry.navobj}
              onClick={onNavigate}
              display="flex"
              alignItems="center"
              gap={2}
              p={2}
              borderRadius="md"
              textDecoration="none"
              _hover={{
                bg: 'whiteAlpha.100',
                _light: { bg: 'blackAlpha.100' },
              }}
            >
              <Tag size="sm" colorScheme="gray" minW="fit-content">
                {entry.type}
              </Tag>
              <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                {entry.label}
              </Text>
            </AngularNavLink>
          );

          if (entityType !== null) {
            return (
              <TrackerWrapper
                key={`${entry.type}-${entry.id}`}
                entityId={entry.id}
                entityType={entityType}
              >
                {link}
              </TrackerWrapper>
            );
          }

          return <Box key={`${entry.type}-${entry.id}`}>{link}</Box>;
        })}
        {canExpand && (
          <Button
            size="xs"
            variant="ghost"
            alignSelf="flex-start"
            onClick={() => setExpanded(true)}
          >
            {t('general.showMore')}
          </Button>
        )}
      </Flex>
    </Box>
  );
}
